import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api, DeepAnalysisExportData } from '../../services/api';
import { exportDeepAnalysisToHTML } from '../../utils/export';

interface DeepAnalysisExportMenuProps {
  exportData: DeepAnalysisExportData;
}

type ExportFormat = 'html' | 'pdf' | 'docx';

export function DeepAnalysisExportMenu({ exportData }: DeepAnalysisExportMenuProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const userTier = user?.tier || 'free';
  const canExportPDF = userTier === 'pro' || userTier === 'max';
  const canExportDOCX = userTier === 'pro' || userTier === 'max';

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format: ExportFormat) => {
    setError(null);

    if (format === 'html') {
      // Client-side export, no tier restriction
      exportDeepAnalysisToHTML(exportData, `analiza-${exportData.sessionId}.html`);
      setIsOpen(false);
      return;
    }

    if (format === 'pdf' && !canExportPDF) {
      setError('Eksport do PDF wymaga planu Pro lub MAX');
      return;
    }

    if (format === 'docx' && !canExportDOCX) {
      setError('Eksport do DOCX wymaga planu Pro lub MAX');
      return;
    }

    setLoadingFormat(format);

    try {
      let blob: Blob;
      let filename: string;

      if (format === 'pdf') {
        blob = await api.exportDeepAnalysisPDF(exportData);
        filename = `analiza-${exportData.sessionId}.pdf`;
      } else {
        blob = await api.exportDeepAnalysisDOCX(exportData);
        filename = `analiza-${exportData.sessionId}.docx`;
      }

      downloadBlob(blob, filename);
      setIsOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.exportError'));
    } finally {
      setLoadingFormat(null);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        {t('common.export')}
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
          <div className="p-2">
            {/* HTML Export - Free */}
            <button
              onClick={() => handleExport('html')}
              disabled={loadingFormat !== null}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-left rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-slate-900 dark:text-white">HTML</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Otworz w przegladarce</div>
                </div>
              </div>
              <span className="px-1.5 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded">
                FREE
              </span>
            </button>

            {/* PDF Export - Pro */}
            <button
              onClick={() => handleExport('pdf')}
              disabled={loadingFormat !== null}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left rounded-md transition-colors disabled:opacity-50 ${
                canExportPDF
                  ? 'hover:bg-slate-100 dark:hover:bg-slate-700'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded">
                  {loadingFormat === 'pdf' ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="font-medium text-slate-900 dark:text-white">PDF</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Dokument do druku</div>
                </div>
              </div>
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                canExportPDF
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                {canExportPDF ? 'PRO' : (
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    PRO
                  </span>
                )}
              </span>
            </button>

            {/* DOCX Export - MAX */}
            <button
              onClick={() => handleExport('docx')}
              disabled={loadingFormat !== null}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left rounded-md transition-colors disabled:opacity-50 ${
                canExportDOCX
                  ? 'hover:bg-slate-100 dark:hover:bg-slate-700'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                  {loadingFormat === 'docx' ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="font-medium text-slate-900 dark:text-white">Word (DOCX)</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Edytowalny dokument</div>
                </div>
              </div>
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                canExportDOCX
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                {canExportDOCX ? 'PRO' : (
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    PRO
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Upgrade prompt for free users */}
          {userTier === 'free' && (
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
              <a
                href="/pricing"
                className="text-xs text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Uaktualnij plan, aby odblokowac wiecej formatow
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeepAnalysisExportMenu;
